import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useUpdateProfile, useChangePassword } from '../../api/auth';
import { useAuth } from '../../auth/AuthContext';
import { NotificationPreferencesCard } from './NotificationPreferencesCard';
import { WhatsappAlertsCard } from './WhatsappAlertsCard';

function ProfileForm() {
  const { user, refreshMe } = useAuth();
  const updateProfile = useUpdateProfile();
  const toast = useToast();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const trimmedName = displayName.trim();
    if (trimmedName.length < 1) {
      setFieldErrors({ displayName: 'Enter your name.' });
      return;
    }

    try {
      await updateProfile.mutateAsync({
        displayName: trimmedName,
        phone: phone.trim() || null,
      });
      await refreshMe();
      toast.success('Profile updated.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not update your profile. Please try again.',
        );
      }
    }
  }

  return (
    <Card title="Profile">
      <form
        style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 420 }}
        onSubmit={onSubmit}
        noValidate
      >
        <TextField label="Email" type="email" value={user?.email ?? ''} disabled readOnly />
        {user?.emailVerifiedAt ? (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}>
            Verified
          </p>
        ) : (
          <p
            style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}
          >
            Not verified yet.
          </p>
        )}
        <TextField
          label="Display name"
          autoComplete="name"
          required
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setFieldErrors((f) => {
              const { displayName: _d, ...rest } = f;
              return rest;
            });
          }}
          error={fieldErrors.displayName}
        />
        <TextField
          label="Phone (optional)"
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setFieldErrors((f) => {
              const { phone: _p, ...rest } = f;
              return rest;
            });
          }}
          error={fieldErrors.phone}
        />
        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div>
          <Button type="submit" disabled={updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ChangePasswordForm() {
  const changePassword = useChangePassword();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (currentPassword.length < 1) errors.currentPassword = 'Enter your current password.';
    if (newPassword.length < 10) errors.newPassword = 'Password must be at least 10 characters.';
    if (confirm !== newPassword) errors.confirm = 'Passwords do not match.';
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      toast.success('Password changed. Your other sessions were signed out.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'AUTH_INVALID_CREDENTIALS') {
        setFieldErrors({ currentPassword: 'Current password is incorrect.' });
      } else if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not change your password. Please try again.',
        );
      }
    }
  }

  return (
    <Card title="Change password">
      <form
        style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 420 }}
        onSubmit={onSubmit}
        noValidate
      >
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            setFieldErrors((f) => {
              const { currentPassword: _c, ...rest } = f;
              return rest;
            });
          }}
          error={fieldErrors.currentPassword}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setFieldErrors((f) => {
              const { newPassword: _n, ...rest } = f;
              return rest;
            });
          }}
          error={fieldErrors.newPassword}
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setFieldErrors((f) => {
              const { confirm: _c, ...rest } = f;
              return rest;
            });
          }}
          error={fieldErrors.confirm}
        />
        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div>
          <Button type="submit" disabled={changePassword.isPending}>
            {changePassword.isPending ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function ProfilePage() {
  const { user } = useAuth();
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 500 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Profile</h1>
      {/* Keyed on the user id so the form's local state (seeded from `user` in
          useState) resets if this mounts before auth finishes bootstrapping -
          useState's initializer only runs once, so without this the fields
          would stay stuck at their empty initial value. */}
      <ProfileForm key={user?.id ?? 'anonymous'} />
      <ChangePasswordForm />
      <NotificationPreferencesCard />
      <WhatsappAlertsCard />
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
